import { Component, OnDestroy, OnInit } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { BehaviorSubject, Subscription } from "rxjs";
import { IncidentService } from "src/app/_share/_services/incident.service";
import { CreateIncidentComponent } from "../create-incident/create-incident.component";
import { EmployeeService } from "src/app/_share/_services/employee.service";
import { TransgressionService } from "src/app/_share/_services/transgression.service";
import { ViewIncidentComponent } from "../view-incident/view-incident.component";
import { FilesService } from "src/app/_share/_services/files.service";

@Component({
  selector: "app-incidents",
  templateUrl: "./incidents.component.html",
  styleUrls: ["./incidents.component.scss"],
  providers: [ConfirmationService, MessageService],
})
export class IncidentsComponent implements OnInit, OnDestroy {
  selectedRowCMObservable$: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  Incidents$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  ref: DynamicDialogRef | undefined;
  incidents: any[] = [];
  incidentsObservable$: any;
  private subscriptions: Subscription = new Subscription();
  employees: any[] = [];
  transgressions: any[] = [];

  constructor(
    public dialogService: DialogService,
    public incidentService: IncidentService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public employeeService: EmployeeService,
    public transgressionService: TransgressionService,
    public FilesService: FilesService
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      this.Incidents$.next(false);
    }, 3000);
    this.subscriptions.add(
      this.incidentService.getIncidents().subscribe((incidents) => {
        this.incidents = incidents;
        console.log("incidents", incidents);
      })
    );
    this.loadEmployees(); // Load employees
    this.loadTransgressions(); // Load transgressions
    this.refreshFiles();
    this.getIncidentsOptionsList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onGlobalSearch(event: any) {
    const searchValue = event.target.value;
    if (searchValue.trim()) {
      // Filter the employees array based on the global search query
      this.incidents = this.filterIncidents(searchValue);
    } else {
      // If search is cleared, load the full list
      this.getIncidentsOptionsList();
    }
  }

  filterIncidents(query: string): any[] {
    return this.incidents.filter((incident) =>
      Object.values(incident).some((value) =>
        String(value).toLowerCase().includes(query.toLowerCase())
      )
    );
  }

  loadEmployees() {
    this.employeeService.getEmployees().subscribe((employees) => {
      this.employees = employees;
    });
  }
  loadTransgressions() {
    this.transgressionService
      .getTransgressions()
      .subscribe((transgressions) => {
        this.transgressions = transgressions;
      });
  }

  getEmployeeNameById(id: number): string {
    const employee = this.employees.find((emp: any) => emp.id === id);
    return employee
      ? `${employee.employee_code}  ${employee.first_name} ${employee.last_name}`
      : "loading...";
  }

  getTransgressionOffenceById(id: number): string {
    const transgression = this.transgressions.find(
      (trans: any) => trans.id === id
    );
    return transgression ? `${transgression.offence}` : "loading...";
  }

  getIncidentsOptionsList() {
    this.subscriptions.add(
      this.incidentService.getIncidents().subscribe((incidents: any[]) => {
        this.incidents = incidents;
      })
    );
  }

  getIncidentStatusSeverity(value: string): string {
    switch (value) {
      case "open":
        return "success";
      case "not-guilty":
      case "acknowledged-verbal-warning":
      case "acknowledged-written-warning":
      case "acknowledged-final written-warning":
      case "acknowledge-pending-hearing":
      case "hearing scheduled":
        return "info";
      case "verbal-warning-issued":
      case "written-warning-issued":
      case "final-written-warning-issued":
        return "warning";
      case "pending-hearing":
        return "danger";
      default:
        return "info";
    }
  }

  columns = [
    { field: "incident_status", header: "Status" },
    { field: "category", header: "Category" },
    { field: "description", header: "Description" },
    { field: "action_taken", header: "Action Taken" },
    { field: "date", header: "Date Of Incident" },
    { field: "resolution", header: "Resolution" },
    { field: "employee_id", header: "Employee" },
    { field: "transgression_id", header: "Transgression" },
    // { field: "files", header: "Incident Evidence Files" }, // New column
  ];

  getFileLinks(files: any[]): any[] {
    if (!files || files.length === 0) {
      return [];
    }
    return files.map((file) => ({
      unique_file_reference: file.unique_file_reference,
      original_filename: file.original_filename,
    }));
  }

  downloadFile(unique_file_reference: string, original_filename: string) {
    this.FilesService.downloadFile(
      unique_file_reference,
      original_filename
    ).subscribe(
      () => {
        console.log("File downloaded successfully");
      },
      (error) => {
        console.error("Error downloading file:", error);
      }
    );
  }
  isLastElement(file: any, filesArray: any[]): boolean {
    return filesArray.indexOf(file) === filesArray.length - 1;
  }

  // Refresh the files list
  refreshFiles() {
    this.subscriptions.add(
      this.FilesService.refreshFiles().subscribe((files) => {
        console.log("Files refreshed", files);
      })
    );
  }

  private refreshIncidentData() {
    this.getIncidentsOptionsList();
    this.refreshFiles();
  }

  viewIncident(rowDatas: any) {
    this.refreshIncidentData();
    this.ref = this.dialogService.open(ViewIncidentComponent, {
      data: {
        incident: rowDatas,
      },
      header: "Incident Details",
      width: "90%",
      height: "70%",
      closeOnEscape: true,
    });

    this.ref.onClose.subscribe((result) => {
      if (result) {
        this.getIncidentsOptionsList();
        this.refreshFiles(); // Refresh files when dialog is closed
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Incident successfully concluded",
        });
      }
    });
  }

  showAdd() {
    // this.refreshIncidentData();
    this.ref = this.dialogService.open(CreateIncidentComponent, {
      data: {
        incidentData: null,
        listOfIncidents: this.incidentService.getIncidents(),
      },
      header: "Add Incident",
      width: "65%",
      height: "70%",
      closeOnEscape: true,
    });

    this.ref.onClose.subscribe((result) => {
      if (result) {
        this.getIncidentsOptionsList();
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Incident successfully added",
        });
      } else {
        // Toast message for closing the dialog without saving
        this.messageService.add({
          severity: "info",
          summary: "Cancelled",
          detail: "Adding incident was cancelled",
        });
      }
    });
  }

  editIncident(rowData: any) {
    this.refreshIncidentData();
    this.ref = this.dialogService.open(CreateIncidentComponent, {
      data: {
        formData: rowData,
      },
      header: "Edit Incident",
      width: "90%",
      height: "75%",
      closeOnEscape: true,
    });

    this.ref.onClose.subscribe((result) => {
      if (result) {
        result.transgression_id = result.transgression_ids;
        console.log("result", result);
        const incidentId = rowData.id;
        this.incidentService
          .updateIncident(incidentId, result)
          .subscribe(() => {
            this.getIncidentsOptionsList();
            this.refreshFiles(); // Refresh files when dialog is closed
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Incident updated successfully",
            });
          });
      } else {
        // Toast message for closing the dialog without saving
        this.messageService.add({
          severity: "info",
          summary: "Cancelled",
          detail: "Editing incident was cancelled",
        });
      }
    });
  }

  deleteIncident(rowData: any) {
    this.confirmationService.confirm({
      message: "Are you sure you want to delete this incident?",
      header: "Delete Confirmation",
      icon: "pi pi-info-circle",
      accept: () => {
        this.incidentService.deleteIncident(rowData.id).subscribe(() => {
          this.getIncidentsOptionsList();
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Incident deleted successfully",
          });
        });
      },
      reject: () => {
        this.messageService.add({
          severity: "info",
          summary: "Rejected",
          detail: "Incident was not deleted",
        });
      },
    });
  }
}
